/**
 * Advanced Route Visualization & Fleet Management
 * Ultra-professional map interface with real-time tracking and route optimization
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Button,
  IconButton,
  Avatar,
  Stack,
  Divider,
  Fade,
  LinearProgress,
  Badge,
  Tooltip,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Map as MapIcon,
  DirectionsCar,
  LocationOn,
  Route,
  Timeline,
  Speed,
  Error as ErrorIcon,
  Refresh,
  Dashboard,
  Analytics,
  TrendingUp,
  PlayArrow,
  Pause,
  ZoomIn,
  MyLocation,
  Layers,
  Settings,
  FilterList,
  Fullscreen,
  AccessTime,
  LocalShipping,
  Business,
  Navigation,
  Home,
  Store,
  Build,
  Assignment,
  Work,
  AltRoute,
  TableView,
  ViewList,
  Search,
  Clear,
} from '@mui/icons-material';
import { InteractiveRouteMap } from '../components/maps/InteractiveRouteMap';
import { AdvancedMapFilter, type AdvancedFilterRule, type LayerVisibility } from '../components/maps/AdvancedMapFilter';
import { useVehicles } from '../hooks/useVehicles';
import { useLocations } from '../hooks/useLocations';
import { useJobs } from '../hooks/useJobs';
import { useVRPData } from '../contexts/VRPDataContext';
import apiClient from '../services/api';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';

// Enhanced theme configuration
const theme = {
  primary: '#2563EB',
  secondary: '#10B981',
  accent: '#F59E0B',
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  surface: '#FFFFFF',
  background: '#F8FAFC',
  gradients: {
    primary: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
    secondary: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    accent: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    purple: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
  },
  shadows: {
    card: '0 1px 3px rgba(0, 0, 0, 0.1)',
    elevated: '0 4px 12px rgba(0, 0, 0, 0.15)',
    float: '0 8px 25px rgba(0, 0, 0, 0.15)',
  }
};

export const MapView: React.FC = () => {
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  const { currentDataset } = useVRPData();
  
  // Data hooks with automatic refetching - only fetch when we have a valid project and context
  const projectId = currentProject?.id || 0;
  const hasContext = !!(currentScenario && currentDataset);
  const { data: vehicles = [], isLoading: vehiclesLoading, error: vehiclesError, refetch: refetchVehicles } = useVehicles(projectId, currentScenario?.id, { enabled: projectId > 0 && hasContext, dataset_id: currentDataset?.id });
  const { data: locations = [], isLoading: locationsLoading, error: locationsError, refetch: refetchLocations } = useLocations(projectId, currentScenario?.id, { enabled: projectId > 0 && hasContext, dataset_id: currentDataset?.id });
  const { data: jobs = [], isLoading: jobsLoading, error: jobsError, refetch: refetchJobs } = useJobs(projectId, currentScenario?.id, { enabled: projectId > 0 && hasContext, dataset_id: currentDataset?.id });
  
  // State management
  const [viewMode, setViewMode] = useState<'overview' | 'routes' | 'tracking' | 'analytics'>('overview');
  const [displayMode, setDisplayMode] = useState<'map' | 'tables'>('map');
  const [isRealTimeActive, setIsRealTimeActive] = useState(true);
  const [mapHeight, setMapHeight] = useState('calc(100vh - 120px)');
  const [optimizationRuns, setOptimizationRuns] = useState<any[]>([]);
  const [selectedOptimizationRun, setSelectedOptimizationRun] = useState<number | undefined>();
  const [routeSteps, setRouteSteps] = useState<any[]>([]);
  const [optimizationRunsLoading, setOptimizationRunsLoading] = useState(false);
  const [rawOptimizationResponse, setRawOptimizationResponse] = useState<any>(null);
  const [forceStraightLines, setForceStraightLines] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(true);

  // Advanced filter state
  const [filterRules, setFilterRules] = useState<AdvancedFilterRule[]>([]);
  const [layerVisibility, setLayerVisibility] = useState<LayerVisibility>({
    showVehicles: true,
    showLocations: true,
    showRoutes: true,
    showJobs: true,
  });
  const [clusteringEnabled, setClusteringEnabled] = useState(true);

  // Table state
  const [tableSearch, setTableSearch] = useState('');
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
  const [tableSortField, setTableSortField] = useState<string>('');
  const [tableSortDirection, setTableSortDirection] = useState<'asc' | 'desc'>('asc');
  const [activeTable, setActiveTable] = useState<'locations' | 'vehicles' | 'jobs'>('locations');

  // Apply advanced filters to data
  const applyFilterRules = useCallback((data: any[], entityType: string) => {
    const relevantRules = filterRules.filter(rule => 
      rule.enabled && 
      (rule.field.includes(entityType) || 
       (entityType === 'vehicles' && ['description', 'profile', 'capacity', 'cost_fixed', 'cost_per_hour', 'max_distance', 'speed_factor'].includes(rule.field)) ||
       (entityType === 'locations' && ['name', 'address', 'location_type', 'location_lat', 'location_lon'].includes(rule.field)) ||
       (entityType === 'jobs' && ['description', 'priority', 'service', 'setup', 'location_lat', 'location_lon'].includes(rule.field)) ||
       (entityType === 'routes' && ['vehicle_id', 'step_type', 'step_order', 'distance', 'duration'].includes(rule.field)))
    );

    if (relevantRules.length === 0) return data;

    return data.filter(item => {
      return relevantRules.every(rule => {
        const value = item[rule.field];
        
        if (value === null || value === undefined) return false;
        
        switch (rule.operator) {
          case 'equals':
            return String(value) === String(rule.value) || Number(value) === Number(rule.value);
          case 'contains':
            return String(value).toLowerCase().includes(String(rule.value).toLowerCase());
          case 'startsWith':
            return String(value).toLowerCase().startsWith(String(rule.value).toLowerCase());
          case 'endsWith':
            return String(value).toLowerCase().endsWith(String(rule.value).toLowerCase());
          case 'gt':
            return Number(value) > Number(rule.value);
          case 'lt':
            return Number(value) < Number(rule.value);
          case 'gte':
            return Number(value) >= Number(rule.value);
          case 'lte':
            return Number(value) <= Number(rule.value);
          case 'between':
            return Number(value) >= Number(rule.value) && Number(value) <= Number(rule.value2);
          default:
            return true;
        }
      });
    });
  }, [filterRules]);

  const filteredVehicles = useMemo(() => {
    if (!layerVisibility.showVehicles) return [];
    if (!Array.isArray(vehicles)) return [];
    return applyFilterRules(vehicles, 'vehicles');
  }, [vehicles, layerVisibility.showVehicles, applyFilterRules]);

  const filteredLocations = useMemo(() => {
    if (!layerVisibility.showLocations) return [];
    if (!Array.isArray(locations)) return [];
    return applyFilterRules(locations, 'locations');
  }, [locations, layerVisibility.showLocations, applyFilterRules]);

  const filteredJobs = useMemo(() => {
    if (!layerVisibility.showJobs) return [];
    if (!Array.isArray(jobs)) return [];
    return applyFilterRules(jobs, 'jobs');
  }, [jobs, layerVisibility.showJobs, applyFilterRules]);

  const filteredRouteSteps = useMemo(() => {
    if (!layerVisibility.showRoutes) return [];
    if (!Array.isArray(routeSteps)) return [];
    return applyFilterRules(routeSteps, 'routes');
  }, [routeSteps, layerVisibility.showRoutes, applyFilterRules]);

  // Enhanced statistics with filtered data
  const analytics = useMemo(() => {
    const activeVehicles = filteredVehicles.filter(v => v.profile === 'driving');
    const depotLocations = filteredLocations.filter(l => l.location_type === 'depot');
    const customerLocations = filteredLocations.filter(l => l.location_type === 'customer');
    const servicePoints = filteredLocations.filter(l => l.location_type === 'service_point');
    
    const totalCapacity = filteredVehicles.reduce((sum, v) => {
      if (Array.isArray(v.capacity)) {
        return sum + v.capacity.reduce((a, b) => a + b, 0);
      }
      return sum + (typeof v.capacity === 'number' ? v.capacity : 0);
    }, 0);

    const averageDistance = filteredVehicles.length > 0 
      ? filteredVehicles.reduce((sum, v) => sum + (v.max_distance || 0), 0) / filteredVehicles.length 
      : 0;

    return {
      fleet: {
        total: filteredVehicles.length,
        active: activeVehicles.length,
        utilization: filteredVehicles.length > 0 ? (activeVehicles.length / filteredVehicles.length * 100) : 0,
        capacity: totalCapacity,
        avgDistance: Math.round(averageDistance / 1000), // Convert to km
      },
      network: {
        total: filteredLocations.length,
        depots: depotLocations.length,
        customers: customerLocations.length,
        servicePoints: servicePoints.length,
      },
      operations: {
        activeJobs: filteredJobs.length,
        completedRoutes: Math.floor(filteredJobs.length * 0.65), // Simulated
        efficiency: 92.5, // Simulated efficiency score
        avgDeliveryTime: 24, // Simulated avg delivery time in minutes
      }
    };
  }, [filteredVehicles, filteredLocations, filteredJobs]);

  // Table data processing
  const processTableData = useCallback((data: any[], searchTerm: string) => {
    if (!searchTerm) return data;
    
    return data.filter(item => {
      const searchableFields = Object.values(item).join(' ').toLowerCase();
      return searchableFields.includes(searchTerm.toLowerCase());
    });
  }, []);

  const sortTableData = useCallback((data: any[], field: string, direction: 'asc' | 'desc') => {
    if (!field) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, []);

  const getTableData = useCallback((type: 'locations' | 'vehicles' | 'jobs') => {
    let data: any[] = [];
    switch (type) {
      case 'locations':
        data = filteredLocations;
        break;
      case 'vehicles':
        data = filteredVehicles;
        break;
      case 'jobs':
        data = filteredJobs;
        break;
    }
    
    const searchFiltered = processTableData(data, tableSearch);
    const sorted = sortTableData(searchFiltered, tableSortField, tableSortDirection);
    
    return {
      data: sorted.slice(tablePage * tableRowsPerPage, (tablePage + 1) * tableRowsPerPage),
      total: sorted.length
    };
  }, [filteredLocations, filteredVehicles, filteredJobs, tableSearch, tableSortField, tableSortDirection, tablePage, tableRowsPerPage, processTableData, sortTableData]);

  const handleTableSort = (field: string) => {
    if (tableSortField === field) {
      setTableSortDirection(tableSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setTableSortField(field);
      setTableSortDirection('asc');
    }
    setTablePage(0);
  };

  const handleTablePageChange = (event: unknown, newPage: number) => {
    setTablePage(newPage);
  };

  const handleTableRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTableRowsPerPage(parseInt(event.target.value, 10));
    setTablePage(0);
  };

  // Fetch optimization runs
  const fetchOptimizationRuns = useCallback(async () => {
    if (!currentProject?.id) return;
    
    try {
      setOptimizationRunsLoading(true);
      console.log('🔍 Fetching optimization runs...');
      const response = await apiClient.getOptimizationRuns(currentProject.id, { limit: 10 });
      
      // Handle various response formats defensively
      let runs: any[] = [];
      if (Array.isArray(response)) {
        runs = response;
      } else if (response?.data && Array.isArray(response.data)) {
        runs = response.data;
      } else if (response) {
        runs = [response];
      }
      
      setOptimizationRuns(runs);
      console.log('✅ Found', runs.length, 'optimization runs');
      
      // Auto-select latest run if none selected
      if (runs.length > 0 && !selectedOptimizationRun) {
        setSelectedOptimizationRun(runs[0].id);
      }
    } catch (error) {
      console.error('❌ Error fetching optimization runs:', error);
    } finally {
      setOptimizationRunsLoading(false);
    }
  }, [currentProject?.id, selectedOptimizationRun]);

  // Fetch route steps and raw optimization response for selected optimization run
  const fetchRouteSteps = useCallback(async () => {
    if (!currentProject?.id || !selectedOptimizationRun) {
      setRouteSteps([]);
      setRawOptimizationResponse(null);
      return;
    }
    
    try {
      console.log('🔍 Fetching route steps and raw response for run:', selectedOptimizationRun);
      
      // Fetch route steps
      const steps = await apiClient.getRouteSteps(currentProject.id);
      const safeSteps = Array.isArray(steps) ? steps : [];
      const filteredSteps = safeSteps.filter(step => step.optimization_run_id === selectedOptimizationRun);
      setRouteSteps(filteredSteps);
      console.log('✅ Found', filteredSteps.length, 'route steps for run', selectedOptimizationRun);
      
      // Get raw optimization response from the already fetched optimization runs with better error handling
      let safeOptimizationRuns: any[] = [];
      if (Array.isArray(optimizationRuns)) {
        safeOptimizationRuns = optimizationRuns;
      } else if (optimizationRuns?.data && Array.isArray(optimizationRuns.data)) {
        safeOptimizationRuns = optimizationRuns.data;
      }
      
      const selectedRun = safeOptimizationRuns.find(run => run && run.id === selectedOptimizationRun);
      if (selectedRun?.raw_response) {
        setRawOptimizationResponse(selectedRun.raw_response);
        console.log('✅ Found raw optimization response with geometry data');
        console.log('🔍 Raw response sample:', selectedRun.raw_response.routes?.[0]?.geometry?.substring(0, 50) + '...');
      } else {
        setRawOptimizationResponse(null);
        console.log('⚠️ No raw optimization response found in selected run');
        console.log('🔍 Available run keys:', selectedRun ? Object.keys(selectedRun) : 'No run found');
      }
    } catch (error) {
      console.error('❌ Error fetching route data:', error);
      setRouteSteps([]);
      setRawOptimizationResponse(null);
    }
  }, [currentProject?.id, selectedOptimizationRun, optimizationRuns]);

  // Refresh all data
  const handleRefreshData = useCallback(async () => {
    console.log('🔄 Refreshing all map data...');
    try {
      await Promise.all([
        refetchVehicles(),
        refetchLocations(), 
        refetchJobs(),
        fetchOptimizationRuns()
      ]);
      console.log('✅ Map data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing map data:', error);
    }
  }, [refetchVehicles, refetchLocations, refetchJobs, fetchOptimizationRuns]);

  // Dynamic height calculation - Map takes most of the screen
  useEffect(() => {
    const updateHeight = () => {
      const headerHeight = 120; // Much smaller header
      const availableHeight = window.innerHeight - headerHeight;
      setMapHeight(`${Math.max(600, availableHeight)}px`);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Auto-refresh data when page becomes visible (user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Page became visible, refreshing data...');
        handleRefreshData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [handleRefreshData]);

  // Load optimization runs when project changes
  useEffect(() => {
    if (currentProject?.id) {
      fetchOptimizationRuns();
    }
  }, [currentProject?.id, fetchOptimizationRuns]);

  // Load route steps when optimization run changes
  useEffect(() => {
    fetchRouteSteps();
  }, [fetchRouteSteps]);

  // Loading state
  const isLoading = vehiclesLoading || locationsLoading || jobsLoading;
  const hasErrors = vehiclesError || locationsError || jobsError;
  const isEmpty = !isLoading && filteredVehicles.length === 0 && filteredLocations.length === 0;

  // Adjust map dimensions - map should always be full width when filter panel is overlaid
  const adjustedMapHeight = useMemo(() => {
    return 'calc(100vh - 140px)';
  }, []);

  const adjustedMapWidth = useMemo(() => {
    return '100%';  // Always full width since filter panel is overlaid
  }, []);

  // Enhanced map configuration with filtered data
  const mapConfig = useMemo(() => {
    const baseConfig = {
      showControls: true,
      enableRealTimeTracking: isRealTimeActive,
      routeSteps: viewMode === 'routes' ? filteredRouteSteps : [],
      showRoutes: viewMode === 'routes' && filteredRouteSteps.length > 0,
      vehicles: filteredVehicles,
      locations: filteredLocations,
      jobs: filteredJobs,
    };

    switch (viewMode) {
      case 'overview':
        return { ...baseConfig, showHeatmaps: true, clusterLocations: true, showLocationIcons: true };
      case 'routes':
        return { ...baseConfig, highlightRoutes: true, showRouteDetails: true, showRoutes: true };
      case 'tracking':
        return { ...baseConfig, enableRealTimeTracking: true, showVehicleStatus: true };
      case 'analytics':
        return { ...baseConfig, showAnalytics: true, showPerformanceMetrics: true };
      default:
        return baseConfig;
    }
  }, [viewMode, isRealTimeActive, filteredRouteSteps, filteredVehicles, filteredLocations, filteredJobs]);


  // Create enhanced subtitle with context information
  const pageSubtitle = useMemo(() => {
    let subtitle = 'Vehicle Routing and Fleet Management';
    if (currentProject) {
      subtitle += ` • ${currentProject.name}`;
    }
    if (currentScenario) {
      subtitle += ` • Scenario: ${currentScenario.name}`;
    }
    if (currentDataset) {
      subtitle += ` • Dataset: ${currentDataset.name}`;
    }
    return subtitle;
  }, [currentProject?.name, currentScenario?.name, currentDataset?.name]);

  return (
    <ProfessionalLayout pageTitle="Map View" pageSubtitle={pageSubtitle}>
      <Container maxWidth={false} sx={{ py: 0, px: 1, maxWidth: 'none' }}>
        {/* Context Warning Alert */}
        {(!currentScenario || !currentDataset) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Incomplete Context</Typography>
            <Typography variant="body2">
              {!currentScenario && !currentDataset 
                ? 'Please select a scenario and dataset to view map data.'
                : !currentScenario 
                ? 'Please select a scenario to proceed.'
                : 'Please select a dataset to load map data.'}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Navigate to: Project → Scenarios → Data Management → Map View
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => window.location.href = '/scenarios'}
                size="small"
                sx={{ mr: 1 }}
              >
                Go to Scenarios
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => window.location.href = '/data-management'}
                size="small"
              >
                Go to Data Management
              </Button>
            </Box>
          </Alert>
        )}
        
        <Box sx={{ width: '100%' }}>
          {/* Minimal Header for Map Workspace */}
          <Box sx={{ mb: 1 }}>
          {/* Compact Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            {/* Left: Quick Stats Only */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                icon={<DirectionsCar sx={{ fontSize: '14px !important' }} />}
                label={`${analytics.fleet.active}/${analytics.fleet.total} vehicles`}
                size="small"
                variant="outlined"
                sx={{ height: 24, fontSize: '0.75rem' }}
              />
              <Chip 
                icon={<LocationOn sx={{ fontSize: '14px !important' }} />}
                label={`${analytics.network.total} locations`}
                size="small"
                variant="outlined"
                sx={{ height: 24, fontSize: '0.75rem' }}
              />
              <Chip 
                icon={<Work sx={{ fontSize: '14px !important' }} />}
                label={`${analytics.operations.activeJobs} jobs`}
                size="small"
                variant="outlined"
                sx={{ height: 24, fontSize: '0.75rem' }}
              />
            </Box>
            
            {/* Right: Action Buttons */}
            <Stack direction="row" spacing={1}>
              <Tooltip title={showFilterPanel ? 'Hide Filters' : 'Show Filters'}>
                <IconButton
                  onClick={() => setShowFilterPanel(!showFilterPanel)}
                  size="small"
                  sx={{
                    bgcolor: showFilterPanel ? theme.primary : 'grey.100',
                    color: showFilterPanel ? 'white' : theme.primary,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: showFilterPanel ? theme.primary : theme.primary,
                      color: 'white',
                    }
                  }}
                >
                  <FilterList sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              
              <Tooltip title={isRealTimeActive ? 'Pause Updates' : 'Resume Updates'}>
                <IconButton
                  onClick={() => setIsRealTimeActive(!isRealTimeActive)}
                  size="small"
                  sx={{
                    bgcolor: isRealTimeActive ? theme.secondary : 'grey.300',
                    color: 'white',
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: isRealTimeActive ? theme.secondary : 'grey.400',
                    }
                  }}
                >
                  {isRealTimeActive ? <Pause sx={{ fontSize: 16 }} /> : <PlayArrow sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Refresh Data">
                <IconButton
                  onClick={handleRefreshData}
                  size="small"
                  sx={{
                    bgcolor: 'grey.100',
                    color: theme.primary,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      bgcolor: theme.primary,
                      color: 'white',
                    }
                  }}
                >
                  <Refresh sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Display Mode Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <ToggleButtonGroup
              value={displayMode}
              exclusive
              onChange={(_, value) => value && setDisplayMode(value)}
              size="small"
              sx={{
                bgcolor: 'white',
                boxShadow: theme.shadows.card,
                borderRadius: 2,
                mr: 2,
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '8px !important',
                  px: 2,
                  py: 0.5,
                  mx: 0.25,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&.Mui-selected': {
                    bgcolor: theme.primary,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.primary
                    }
                  }
                }
              }}
            >
              <ToggleButton value="map">
                <MapIcon sx={{ mr: 0.5, fontSize: 16 }} />
                Map View
              </ToggleButton>
              <ToggleButton value="tables">
                <TableView sx={{ mr: 0.5, fontSize: 16 }} />
                Data Tables
              </ToggleButton>
            </ToggleButtonGroup>

          {/* View Mode Controls */}
          {displayMode === 'map' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, value) => value && setViewMode(value)}
                size="small"
                sx={{
                  bgcolor: 'white',
                  boxShadow: theme.shadows.card,
                  borderRadius: 3,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: '12px !important',
                    px: 3,
                    py: 1,
                    mx: 0.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&.Mui-selected': {
                      bgcolor: theme.primary,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.primary
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="overview">
                  <Dashboard sx={{ mr: 1, fontSize: 18 }} />
                  Overview
                </ToggleButton>
                <ToggleButton value="routes">
                  <Route sx={{ mr: 1, fontSize: 18 }} />
                  Routes
                </ToggleButton>
                <ToggleButton value="analytics">
                  <Analytics sx={{ mr: 1, fontSize: 18 }} />
                  Analytics
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Route View Controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
              {/* Route View Toggle - Only show when routes are available */}
              {viewMode === 'routes' && routeSteps.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Route View:
                  </Typography>
                  <ToggleButtonGroup
                    value={forceStraightLines ? 'straight' : 'actual'}
                    exclusive
                    onChange={(_, value) => {
                      if (value !== null) {
                        setForceStraightLines(value === 'straight');
                      }
                    }}
                    size="small"
                    sx={{
                      bgcolor: 'white',
                      boxShadow: theme.shadows.card,
                      borderRadius: 2,
                      '& .MuiToggleButton-root': {
                        border: 'none',
                        borderRadius: '8px !important',
                        px: 2,
                        py: 0.5,
                        mx: 0.25,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&.Mui-selected': {
                          bgcolor: theme.primary,
                          color: 'white',
                          '&:hover': {
                            bgcolor: theme.primary
                          }
                        }
                      }
                    }}
                  >
                    <ToggleButton value="actual">
                      <Tooltip title="Show actual road routes from optimization">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AltRoute sx={{ fontSize: 14 }} />
                          Actual Routes
                        </Box>
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="straight">
                      <Tooltip title="Show straight-line connections between locations">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Timeline sx={{ fontSize: 14 }} />
                          Straight Lines
                        </Box>
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
            </Box>
            </Box>
          )}
          </Box>
        </Box>

        {/* Content Container */}
        <Box sx={{ position: 'relative', width: '100%' }}>
          {displayMode === 'map' ? (
            <>
            {/* Main Map Container */}
          <Paper 
            elevation={0} 
            sx={{ 
              width: '100%',
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: theme.shadows.elevated,
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            {!currentProject ? (
              <Alert 
                severity="info" 
                sx={{ 
                  borderRadius: 4,
                  border: 'none',
                  bgcolor: '#EBF8FF',
                  '& .MuiAlert-icon': { color: theme.primary }
                }}
              >
                <AlertTitle sx={{ color: theme.primary, fontWeight: 700 }}>
                  No Project Selected
                </AlertTitle>
                <Typography sx={{ color: theme.primary }}>
                  Please select a project to view your fleet operations map.
                </Typography>
              </Alert>
            ) : hasErrors ? (
              <Alert 
                severity="error"
                sx={{ borderRadius: 4, border: 'none' }}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => window.location.reload()}
                    sx={{ fontWeight: 600 }}
                  >
                    Retry
                  </Button>
                }
              >
                <AlertTitle sx={{ fontWeight: 700 }}>System Error</AlertTitle>
                Unable to load map data. Please check your connection and try again.
              </Alert>
            ) : isEmpty ? (
              <Box 
                sx={{ 
                  p: 8, 
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #F8FAFC 0%, #EDF2F7 100%)',
                  height: adjustedMapHeight,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: theme.primary, 
                    mx: 'auto', 
                    mb: 3
                  }}
                >
                  <MapIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  No Data Available
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Add vehicles and locations to your project to see them on the map.
                </Typography>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ 
                    background: theme.gradients.primary,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Add Your First Vehicle
                </Button>
              </Box>
            ) : isLoading ? (
              <Box
                sx={{
                  height: adjustedMapHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 3,
                  background: 'linear-gradient(135deg, #F8FAFC 0%, #EDF2F7 100%)'
                }}
              >
                <CircularProgress 
                  size={80} 
                  thickness={3}
                  sx={{ color: theme.primary }}
                />
                <Typography variant="h6" color="text.secondary" fontWeight={600}>
                  Loading Fleet Operations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Initializing map data and vehicle positions...
                </Typography>
              </Box>
            ) : (
              <InteractiveRouteMap
                height={adjustedMapHeight}
                showControls={mapConfig.showControls}
                enableRealTimeTracking={mapConfig.enableRealTimeTracking}
                routeSteps={mapConfig.routeSteps}
                showRoutes={mapConfig.showRoutes}
                viewMode={viewMode}
                selectedOptimizationRun={selectedOptimizationRun}
                rawOptimizationResponse={rawOptimizationResponse}
                forceStraightLines={forceStraightLines}
                clusteringEnabled={clusteringEnabled}
                vehicles={filteredVehicles}
                locations={filteredLocations}
                jobs={filteredJobs}
              />
            )}
          </Paper>

          {/* Advanced Filter Panel - Always rendered but conditionally visible */}
          <AdvancedMapFilter
            filterRules={filterRules}
            onFilterRulesChange={setFilterRules}
            layerVisibility={layerVisibility}
            onLayerVisibilityChange={setLayerVisibility}
            clusteringEnabled={clusteringEnabled}
            onClusteringChange={setClusteringEnabled}
            vehicles={vehicles}
            locations={locations}
            jobs={jobs}
            routes={filteredRouteSteps}
            totalVehicles={vehicles.length}
            totalLocations={locations.length}
            totalJobs={jobs.length}
            totalRoutes={routeSteps.length}
            height={adjustedMapHeight}
            expanded={showFilterPanel}
            onExpandedChange={setShowFilterPanel}
          />
            </>
          ) : (
            /* Data Tables View */
            <Paper 
              elevation={0} 
              sx={{ 
                width: '100%',
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: theme.shadows.elevated,
                border: '1px solid rgba(0,0,0,0.06)',
                minHeight: adjustedMapHeight,
              }}
            >
              {!currentProject ? (
                <Alert 
                  severity="info" 
                  sx={{ 
                    borderRadius: 4,
                    border: 'none',
                    bgcolor: '#EBF8FF',
                    '& .MuiAlert-icon': { color: theme.primary }
                  }}
                >
                  <AlertTitle sx={{ color: theme.primary, fontWeight: 700 }}>
                    No Project Selected
                  </AlertTitle>
                  <Typography sx={{ color: theme.primary }}>
                    Please select a project to view your data tables.
                  </Typography>
                </Alert>
              ) : (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Table Controls */}
                  <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Data Tables
                      </Typography>
                      
                      {/* Search */}
                      <TextField
                        size="small"
                        placeholder="Search data..."
                        value={tableSearch}
                        onChange={(e) => {
                          setTableSearch(e.target.value);
                          setTablePage(0);
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: tableSearch && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setTableSearch('')}>
                                <Clear fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 250 }}
                      />
                    </Box>
                    
                    {/* Table Type Tabs */}
                    <Tabs
                      value={activeTable}
                      onChange={(_, value) => {
                        setActiveTable(value);
                        setTablePage(0);
                        setTableSearch('');
                      }}
                      sx={{
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 600,
                          minHeight: 36,
                        }
                      }}
                    >
                      <Tab
                        value="locations"
                        label={`Locations (${filteredLocations.length})`}
                        icon={<LocationOn sx={{ fontSize: 16 }} />}
                        iconPosition="start"
                      />
                      <Tab
                        value="vehicles"
                        label={`Vehicles (${filteredVehicles.length})`}
                        icon={<DirectionsCar sx={{ fontSize: 16 }} />}
                        iconPosition="start"
                      />
                      <Tab
                        value="jobs"
                        label={`Jobs (${filteredJobs.length})`}
                        icon={<Work sx={{ fontSize: 16 }} />}
                        iconPosition="start"
                      />
                    </Tabs>
                  </Box>

                  {/* Table Content */}
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {activeTable === 'locations' && (
                      <LocationsDataTable 
                        data={getTableData('locations')}
                        onSort={handleTableSort}
                        sortField={tableSortField}
                        sortDirection={tableSortDirection}
                      />
                    )}
                    {activeTable === 'vehicles' && (
                      <VehiclesDataTable 
                        data={getTableData('vehicles')}
                        onSort={handleTableSort}
                        sortField={tableSortField}
                        sortDirection={tableSortDirection}
                      />
                    )}
                    {activeTable === 'jobs' && (
                      <JobsDataTable 
                        data={getTableData('jobs')}
                        onSort={handleTableSort}
                        sortField={tableSortField}
                        sortDirection={tableSortDirection}
                      />
                    )}
                  </Box>

                  {/* Pagination */}
                  <TablePagination
                    component="div"
                    count={getTableData(activeTable).total}
                    page={tablePage}
                    rowsPerPage={tableRowsPerPage}
                    onPageChange={handleTablePageChange}
                    onRowsPerPageChange={handleTableRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{ borderTop: '1px solid rgba(0,0,0,0.12)' }}
                  />
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </Container>
    </ProfessionalLayout>
  );
};

// Table Components for Data View
interface TableDataProps {
  data: { data: any[]; total: number };
  onSort: (field: string) => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
}

const LocationsDataTable: React.FC<TableDataProps> = ({ data, onSort, sortField, sortDirection }) => {
  const formatCoordinates = (lat?: number, lon?: number) => {
    if (lat && lon) {
      return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    }
    return 'N/A';
  };

  const formatTimeWindows = (timeWindows?: any[]) => {
    if (!timeWindows || timeWindows.length === 0) return 'N/A';
    return timeWindows.map(tw => `${tw.start || 0}-${tw.end || 86400}`).join(', ');
  };

  return (
    <TableContainer sx={{ height: '100%' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortField === 'name'}
                direction={sortField === 'name' ? sortDirection : 'asc'}
                onClick={() => onSort('name')}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'location_type'}
                direction={sortField === 'location_type' ? sortDirection : 'asc'}
                onClick={() => onSort('location_type')}
              >
                Type
              </TableSortLabel>
            </TableCell>
            <TableCell>Address</TableCell>
            <TableCell>Coordinates</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.data.map((location) => (
            <TableRow key={location.id} hover>
              <TableCell sx={{ fontWeight: 600 }}>{location.name}</TableCell>
              <TableCell>
                <Chip 
                  label={location.location_type || 'Unknown'} 
                  size="small" 
                  color={location.location_type === 'depot' ? 'primary' : 'default'}
                />
              </TableCell>
              <TableCell>{location.address || 'N/A'}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {formatCoordinates(location.location_lat, location.location_lon)}
              </TableCell>
              <TableCell>
                {location.created_at ? new Date(location.created_at).toLocaleDateString() : 'N/A'}
              </TableCell>
            </TableRow>
          ))}
          {data.data.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No locations found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const VehiclesDataTable: React.FC<TableDataProps> = ({ data, onSort, sortField, sortDirection }) => {
  const formatCapacity = (capacity?: number[]) => {
    if (!capacity || capacity.length === 0) return 'N/A';
    return capacity.join(', ');
  };

  const formatSkills = (skills?: number[]) => {
    if (!skills || skills.length === 0) return 'None';
    return skills.join(', ');
  };

  return (
    <TableContainer sx={{ height: '100%' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortField === 'description'}
                direction={sortField === 'description' ? sortDirection : 'asc'}
                onClick={() => onSort('description')}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'profile'}
                direction={sortField === 'profile' ? sortDirection : 'asc'}
                onClick={() => onSort('profile')}
              >
                Profile
              </TableSortLabel>
            </TableCell>
            <TableCell>Capacity</TableCell>
            <TableCell>Start Location</TableCell>
            <TableCell>Skills</TableCell>
            <TableCell>Max Distance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.data.map((vehicle) => (
            <TableRow key={vehicle.id} hover>
              <TableCell sx={{ fontWeight: 600 }}>{vehicle.description}</TableCell>
              <TableCell>
                <Chip 
                  label={vehicle.profile || 'driving'} 
                  size="small" 
                  color="primary"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{formatCapacity(vehicle.capacity)}</TableCell>
              <TableCell>
                {vehicle.start_location?.name || 
                 `${vehicle.start_lat?.toFixed(4) || 'N/A'}, ${vehicle.start_lon?.toFixed(4) || 'N/A'}`}
              </TableCell>
              <TableCell>{formatSkills(vehicle.skills)}</TableCell>
              <TableCell>
                {vehicle.max_distance ? `${(vehicle.max_distance / 1000).toFixed(1)} km` : 'Unlimited'}
              </TableCell>
            </TableRow>
          ))}
          {data.data.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No vehicles found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const JobsDataTable: React.FC<TableDataProps> = ({ data, onSort, sortField, sortDirection }) => {
  const formatTime = (seconds?: number) => {
    if (!seconds) return '0 min';
    return `${Math.round(seconds / 60)} min`;
  };

  const formatSkills = (skills?: number[]) => {
    if (!skills || skills.length === 0) return 'None';
    return skills.join(', ');
  };

  const formatDelivery = (delivery?: number[]) => {
    if (!delivery || delivery.length === 0) return 'N/A';
    return delivery.join(', ');
  };

  return (
    <TableContainer sx={{ height: '100%' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortField === 'description'}
                direction={sortField === 'description' ? sortDirection : 'asc'}
                onClick={() => onSort('description')}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell>Location</TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'service'}
                direction={sortField === 'service' ? sortDirection : 'asc'}
                onClick={() => onSort('service')}
              >
                Service Time
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'setup'}
                direction={sortField === 'setup' ? sortDirection : 'asc'}
                onClick={() => onSort('setup')}
              >
                Setup Time
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'priority'}
                direction={sortField === 'priority' ? sortDirection : 'asc'}
                onClick={() => onSort('priority')}
              >
                Priority
              </TableSortLabel>
            </TableCell>
            <TableCell>Delivery</TableCell>
            <TableCell>Skills Required</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.data.map((job) => (
            <TableRow key={job.id} hover>
              <TableCell sx={{ fontWeight: 600 }}>{job.description}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {job.location_lat && job.location_lon 
                  ? `${job.location_lat.toFixed(4)}, ${job.location_lon.toFixed(4)}`
                  : 'N/A'}
              </TableCell>
              <TableCell>{formatTime(job.service)}</TableCell>
              <TableCell>{formatTime(job.setup)}</TableCell>
              <TableCell>
                {job.priority ? (
                  <Chip 
                    label={job.priority} 
                    size="small" 
                    color={job.priority > 50 ? 'error' : job.priority > 20 ? 'warning' : 'default'}
                  />
                ) : (
                  'Normal'
                )}
              </TableCell>
              <TableCell>{formatDelivery(job.delivery)}</TableCell>
              <TableCell>{formatSkills(job.skills)}</TableCell>
            </TableRow>
          ))}
          {data.data.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No jobs found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};